import { DataTable, DataTableColumn, SelectionModel, useContainerHeight, EmptyState } from '@components/DataTable';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import LinkedinSmallIcon from '@components/Icon/LinkedinSmallIcon';
import StarIcon from '@components/Icon/Star';
import StarActiveIcon from '@components/Icon/StarActiveIcon';
import { useMutation } from '@apollo/client';
import { UPDATE_JOB_APPLICATION_RATING } from '@gql/jobs';
import TalentMediaDialog from '@modules/talent-media/TalentMediaDialog';
import { JobApplicationDisplayMode, JobApplicationRaw, getHighestDegreeLabel } from '@modules/jobApplications/types';
import { ScoringStatus } from '@modules/scoring/types';
import ScoringCell from '@modules/jobApplications/components/ScoringCell';
import AvatarStatusFavorite from '@modules/talents/components/AvatarStatusFavorite';
import { useTalentOperations } from '@modules/talents/context/TalentOperationsContext';
import { useJobApplicationsOperations } from '@modules/jobApplications/context/JobApplicationsOperationsContext';
import { Avatar, Box, ButtonBase, Rating, Typography } from '@mui/material';
import { startCase } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import FloatingSelectionBar from './components/FloatingSelectionBar';
import ChangeJobApplicationStatusButton from './components/ChangeJobApplicationStatusButton';
import JobApplicationDetailDialog from './JobApplicationDetail/JobApplicationDetailDialog';

interface JobApplicationListProps {
    data: JobApplicationRaw[];
    meta?: {
        totalDocs?: number;
        page?: number;
        limit?: number;
    };
    currentPage?: number;
    itemsPerPage?: number;
    onPageChange?: (page: number) => void;
    onItemsPerPageChange?: (limit: number) => void;
    onSortChange?: (sortBy: { field: string; order: number }) => void;
    headerColumnList: {
        id: number;
        title: string;
    }[];
    onConfirm: (params: {
        title: string;
        description: string;
        confirmTitle: string;
        cancelTitle: string;
        action: string;
    }) => void;
    loading: boolean;
    isLoadingMore?: boolean;
    onScrollEnd?: () => void;
    /**
     * Display mode for the table rows
     * - 'full': Show both talent and job offer details
     * - 'job-focus': Emphasize job offer details (talent detail page)
     * - 'talent-focus': Emphasize talent details (job offer detail page)
     */
    displayMode?: JobApplicationDisplayMode;
}

const JobApplicationDataTable = ({
    data,
    meta,
    currentPage = 1,
    itemsPerPage = 10,
    onPageChange,
    onItemsPerPageChange,
    onSortChange,
    loading,
    isLoadingMore,
    onScrollEnd,
    displayMode = 'talent-focus',
}: JobApplicationListProps) => {
    const { updateStatus } = useTalentOperations();
    const { showSnackbar } = useSnackbar();
    const [updateJobApplicationRatingMutation] = useMutation(UPDATE_JOB_APPLICATION_RATING);
    const {
        selectedJobApplicationIds,
        addJobApplication,
        removeJobApplication,
        clearSelection,
    } = useJobApplicationsOperations();

    const [detailDialogId, setDetailDialogId] = useState<string | null>(null);

    const [mediaDialogState, setMediaDialogState] = useState<{
        isOpen: boolean;
        talentId: string;
        jobApplicationId?: string;
        defaultTab: 'notes' | 'files' | 'comments' | 'scoring';
        score?: number | null;
        scoreExplainer?: string;
        scoringStatus?: ScoringStatus;
        jobOfferId?: string;
        jobOfferTitle?: string;
        jobOfferNumber?: string | number;
    }>({
        isOpen: false,
        talentId: '',
        jobApplicationId: undefined,
        defaultTab: 'notes',
    });

    // Derive SelectionModel from context
    const selectionModel: SelectionModel = useMemo(() => ({
        selectedRows: selectedJobApplicationIds,
        isAllSelected: data.length > 0 && selectedJobApplicationIds.length === data.length,
        isSomeSelected: selectedJobApplicationIds.length > 0 && selectedJobApplicationIds.length < data.length,
    }), [selectedJobApplicationIds, data.length]);

    // Use backend pagination meta when available (server-side pagination)
    const totalDocs = meta?.totalDocs ?? data?.length ?? 0;
    const page = meta?.page ?? currentPage;
    const pageSize = meta?.limit ?? itemsPerPage;

    // Get selected job application objects (for FloatingSelectionBar)
    const selectedTalentObjects = useMemo(
        () => data.filter(app => selectedJobApplicationIds.includes(String(app._id))),
        [data, selectedJobApplicationIds]
    );

    // Sync DataTable checkbox selection changes to the context
    const handleSelectionModelChange = useCallback((newModel: SelectionModel) => {
        const prevSet = new Set(selectedJobApplicationIds);
        const newSet = new Set(newModel.selectedRows);

        // Add newly selected rows to context
        newModel.selectedRows
            .filter(id => !prevSet.has(String(id)))
            .forEach(id => {
                const strId = String(id);
                const app = data.find(a => String(a._id) === strId);
                addJobApplication(strId, app?.status, app?.talent?._id);
            });

        // Remove deselected rows from context
        selectedJobApplicationIds
            .filter(id => !newSet.has(id))
            .forEach(id => removeJobApplication(id));
    }, [selectedJobApplicationIds, addJobApplication, removeJobApplication, data]);

    // Handle floating bar close (clear all selections)
    const handleSendMessage = () => {
        // TODO: Implement send message functionality
        console.log('Sending message to selected talents :', selectedTalentObjects);
    };

    // Handle pagination changes
    const handlePageChange = (page: number) => {
        if (onPageChange) {
            onPageChange(page);
        }
    };

    const handlePageSizeChange = (newPageSize: number) => {
        if (onItemsPerPageChange) {
            onItemsPerPageChange(newPageSize);
        }
        if (onPageChange) {
            onPageChange(1);
        }
    };

    const handleSortModelChange = useCallback(
        (model: { field: string; sort: 'asc' | 'desc' | null }[]) => {
            if (!onSortChange) return;

            const activeSort = model[0];
            if (!activeSort?.sort) {
                onSortChange({ field: 'date', order: -1 });
                return;
            }

            onSortChange({ field: activeSort.field, order: activeSort.sort === 'asc' ? 1 : -1 });
        },
        [onSortChange]
    );

    const handleOpenMediaDialog = useCallback(
        (row: JobApplicationRaw, defaultTab: 'notes' | 'files' | 'scoring') => {
            const talentId = row.talent?._id;

            if (!talentId) {
                return;
            }

            const scoringStatus: ScoringStatus =
                row.resumeScore != null ? 'done' : 'pending';

            setMediaDialogState({
                isOpen: true,
                talentId,
                jobApplicationId: String(row._id),
                defaultTab,
                score: row.resumeScore,
                scoreExplainer: row.resumeExplainer ?? undefined,
                scoringStatus,
                jobOfferId: row.jobOffer?._id,
                jobOfferTitle: row.jobOffer?.title,
                jobOfferNumber: row.jobOffer?.jobNumber,
            });
        },
        []
    );

    const handleCloseMediaDialog = useCallback(() => {
        setMediaDialogState(prev => ({
            ...prev,
            isOpen: false,
        }));
    }, []);

    const handleUpdateJobApplicationRating = useCallback(
        (jobApplicationId: string, rating: number) => {
            updateJobApplicationRatingMutation({
                variables: {
                    jobApplicationId,
                    rating,
                },
                update: (cache, { data: mutationData }) => {
                    const updatedJobApplication = mutationData?.updateJobApplicationRatingUpercut;

                    if (!updatedJobApplication) {
                        return;
                    }

                    const cacheId = cache.identify({
                        __typename: 'JobApplication',
                        _id: updatedJobApplication._id,
                    });

                    if (!cacheId) {
                        return;
                    }

                    cache.modify({
                        id: cacheId,
                        fields: {
                            upercutAverageRating() {
                                return updatedJobApplication.upercutAverageRating;
                            },
                            upercutRatings() {
                                return updatedJobApplication.upercutRatings;
                            },
                        },
                    });
                },
                onCompleted: () => {
                    showSnackbar('La note de la candidature a bien ete mise a jour', 'success');
                },
                onError: (error) => {
                    showSnackbar(error.message, 'error');
                },
            });
        },
        [showSnackbar, updateJobApplicationRatingMutation]
    );

    // Define DataTable columns
    const columns: DataTableColumn[] = useMemo(() => [
        {
            id: 'fullName',
            label: 'Nom',
            field: 'fullName',
            sortable: true,
            sticky: true,
            renderCell: ({ row }) => {
                // Get talent and job offer data
                const talent = row.talent;
                const jobOffer = row.jobOffer;
                const talentName = talent?.firstName && talent?.lastName
                    ? `${talent.firstName} ${talent.lastName}`
                    : talent?.email || 'N/A';
                const talentAvatar = talent?.photo || talent?.logo || '';
                const jobOfferTitle = jobOffer?.title || 'N/A';
                const talentId = talent?._id;
                const companyLogo = jobOffer?.brand?.brandLogo?.url || jobOffer?.pro?.brandLogo?.url;

                // Determine what to display based on displayMode
                let primaryName: string;
                let secondaryName: string | null = null;
                let avatar: string;
                let isActive: boolean;

                if (displayMode === 'job-focus') {
                    // Show job offer title and company logo
                    primaryName = jobOfferTitle;
                    avatar = companyLogo;
                    isActive = jobOffer?.active ?? true;
                } else if (displayMode === 'talent-focus') {
                    // Show talent name and talent image
                    primaryName = talentName;
                    avatar = talentAvatar;
                    isActive = talent?.active ?? true;
                } else {
                    // 'full' mode: Show both talent name AND job offer title
                    primaryName = talentName;
                    secondaryName = jobOfferTitle;
                    avatar = talentAvatar;
                    isActive = talent?.active ?? true;
                }

                const handleOpenDetail = (e: React.MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDetailDialogId(String(row._id));
                };

                return (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}>
                        <AvatarStatusFavorite
                            talentIds={talentId}
                            image={avatar}
                            alt={primaryName}
                            currentStatus={displayMode !== 'job-focus' ? talent?.currentStatusUser : undefined}
                            showStatus={displayMode !== 'job-focus'}
                            upercutFavorites={displayMode !== 'job-focus' ? talent?.upercutFavorites : []}
                            onStatusChange={displayMode !== 'job-focus' ? (newStatus) => {
                                if (talentId) {
                                    const backendStatus = newStatus === 'nothing' ? '' : newStatus;
                                    updateStatus([talentId], backendStatus);
                                }
                            } : undefined}
                            size="sm"
                            sx={{ mr: '8px' }}
                            shouldStopPropagation
                        />
                        <Box
                            sx={{
                                color: theme => theme.palette.secondary.main,
                                fontWeight: 700,
                                fontSize: '16px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px',
                            }}>
                            <Box
                                onClick={handleOpenDetail}
                                sx={{
                                    textDecoration: isActive ? 'underline' : 'none',
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    color: theme => theme.palette.secondary.main,
                                    cursor: 'pointer',
                                }}>
                                <Typography fontSize="16px" fontWeight={700}>
                                    {primaryName}
                                </Typography>
                            </Box>
                            {secondaryName && (
                                <Typography
                                    fontSize="14px"
                                    fontWeight={400}
                                    sx={{
                                        color: theme => theme.palette.lightGrey.main,
                                        display: '-webkit-box',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        mt: '2px',
                                    }}>
                                    {secondaryName}
                                </Typography>
                            )}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: theme => theme.palette.lightGrey.main,
                                    mt: secondaryName ? '2px' : 0,
                                }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <ButtonBase
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenMediaDialog(row as JobApplicationRaw, 'notes');
                                        }}>
                                        <i className="fi-br-notebook" style={{ fontSize: '14px', marginRight: '4px' }} />
                                        {row.upercutNotes?.length || 0}
                                    </ButtonBase>
                                    <ButtonBase
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenMediaDialog(row as JobApplicationRaw, 'files');
                                        }}>
                                        <i className="fi-br-clip" style={{ fontSize: '14px', marginRight: '4px' }} />
                                        {talent?.upercutFiles?.length || 0}
                                    </ButtonBase>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                );
            },
        },
        {
            id: 'resumeScore',
            label: 'Score IA',
            field: 'resumeScore',
            sortable: true,
            renderCell: ({ row }) => (
                <ScoringCell
                    row={row as JobApplicationRaw}
                    onOpenScoring={() => handleOpenMediaDialog(row as JobApplicationRaw, 'scoring')}
                />
            ),
        },
        {
            id: 'jobTitle',
            label: "Offre d'emploi",
            field: 'jobTitle',
            sortable: true,
            renderCell: ({ row }) => {
                const jobOfferId = row.jobOffer?.jobNumber;
                const jobOfferTitle = row.jobOffer?.title || '-';

                return (
                    <Typography
                        fontSize="14px"
                        component={Link}
                        to={jobOfferId ? `/job-offers/${jobOfferId}/detail` : '#'}
                        state={{ data: row }}
                        sx={{
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            color: 'inherit',
                        }}>
                        {jobOfferTitle}
                    </Typography>
                );
            },
        },
        {
            id: "companyName",
            label: "Entreprise",
            field: "companyName",
            minWidth: 250,
            wrap: true,
            sortable: true,
            renderCell: ({ row }) => {
                const isBrand = !!row.jobOffer?.brand;
                const brandId = row.jobOffer?.brand?._id || row.jobOffer?.pro?._id;
                const brandName = row.jobOffer?.brand?.brandName || row.jobOffer?.pro?.commercialName || '-';
                const brandLogo = row.jobOffer?.brand?.brandLogo?.url || row.jobOffer?.pro?.brandLogo?.url;
                const navigateTo = isBrand ? `/brands/${brandId}/detail` : `/companies/${brandId}/detail`;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Avatar
                            src={brandLogo}
                            alt={brandName}
                            sx={{ width: 24, height: 24, mr: '8px' }}
                        />
                        <Typography
                            fontSize="14px"
                            component={Link}
                            to={navigateTo}
                            sx={{
                                textDecoration: brandId ? 'underline' : 'none',
                                cursor: brandId ? 'pointer' : 'default',
                                color: brandId ? 'inherit' : 'text.secondary',
                            }}>
                            {brandName}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            id: 'offerCity',
            label: "Ville de l'offre",
            field: 'offerCity',
            sortable: true,
            renderCell: ({ row }) => (
                <Typography fontSize="14px">{startCase(row.jobOffer?.city?.toLowerCase() || '-')}</Typography>
            ),
        },
        {
            id: 'experience',
            label: 'Exp.',
            field: 'yearsOfExperience',
            sortable: true,
            renderCell: ({ row }) => {
                const years = row.talent?.yearsOfExperience;
                if (years == null) return <Typography fontSize="14px">-</Typography>;
                return <Typography fontSize="14px">{years} an{years > 1 ? 's' : ''}</Typography>;
            },
        },
        {
            id: 'education',
            label: 'Etude',
            field: 'education',
            sortable: true,
            renderCell: ({ row }) => (
                <Typography fontSize="14px">
                    {getHighestDegreeLabel(row.talent?.degree || [])}
                </Typography>
            ),
        },
        {
            id: 'previousJob',
            label: 'Dernier emploi',
            field: 'previousJob',
            sortable: false,
            renderCell: ({ row }) => {
                const exps = row.talent?.experiences;
                if (!exps || exps.length === 0) return <Typography fontSize="14px" color="text.secondary">-</Typography>;
                const latest = exps[0];
                const label = [latest.title, latest.enterprise?.name].filter(Boolean).join(' · ');
                return (
                    <Typography
                        fontSize="14px"
                        sx={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            overflow: 'hidden',
                        }}>
                        {label || '-'}
                    </Typography>
                );
            },
        },
        {
            id: 'talentCity',
            label: 'Ville du talent',
            field: 'talentCity',
            sortable: true,
            renderCell: ({ row }) => (
                <Typography fontSize="14px">{row.talent?.city || '-'}</Typography>
            ),
        },
        {
            id: 'professions',
            label: 'Fonctions',
            field: 'professions',
            sortable: true,
            renderCell: ({ row }) => {
                const profession = row.talent?.principalProfession?.professionName;
                return (
                    <Typography
                        fontSize="14px"
                        sx={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            overflow: 'hidden',
                        }}>
                        {profession || '-'}
                    </Typography>
                );
            },
        },
        {
            id: 'rating',
            label: 'Évaluation',
            field: 'upercutAverageRating',
            sortable: true,
            renderCell: ({ row }) => {
                const jobApplicationId = String(row._id);

                return (
                    <Box
                        sx={{
                            padding: '6px 8px',
                            background: theme => theme.palette.background.default,
                            borderRadius: '8px',
                            display: 'inline-block',
                        }}>
                        <Rating
                            value={row.upercutAverageRating ?? 0}
                            sx={{ color: '#ffd700', display: 'flex', alignItems: 'center', gap: '8px' }}
                            icon={<StarActiveIcon />}
                            emptyIcon={<StarIcon />}
                            onChange={(_event, newValue) => {
                                handleUpdateJobApplicationRating(jobApplicationId, newValue || 0);
                            }}
                        />
                    </Box>
                );
            },
        },
        {
            id: 'reference',
            label: 'Source',
            field: 'reference',
            sortable: true,
            renderCell: ({ row }) => {
                const source = row.reference || '-';
                const isLinkedIn = source.toLowerCase().includes('linkedin');
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {isLinkedIn && <LinkedinSmallIcon />}
                        <Typography fontSize="14px">{source}</Typography>
                    </Box>
                );
            }
        },
        {
            id: 'receivedAt',
            label: 'Reçu le',
            field: 'date',
            sortable: true,
            renderCell: ({ row }) => {
                const date = new Date(row.date);
                const formattedDate = date.toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: '2-digit',
                });
                const formattedTime = date.toLocaleTimeString('fr-FR', {
                    hour: '2-digit',
                    minute: '2-digit',
                });
                return <Typography fontSize="14px">{`${formattedDate} ${formattedTime}`}</Typography>;
            },
        },
        {
            id: 'status',
            label: 'Statut',
            field: 'status',
            sortable: true,
            stickyRight: true,
            renderCell: ({ row }) => {
                if (!row.status) return <Typography fontSize="14px" color="text.secondary">-</Typography>;
                return (
                    <ChangeJobApplicationStatusButton
                        jobApplicationId={String(row._id)}
                        status={row.status}
                        subStatus={row.subStatus}
                    />
                );
            },
        },
    ], [displayMode, handleOpenMediaDialog, handleUpdateJobApplicationRating, updateStatus]);

    // Empty state component
    const emptyStateComponent = <EmptyState title="Nous n'avons actuellement aucun candidat" subtitle="Commençons par inviter de nouveaux talents à s'inscrire sur Upercut." />;
    const { containerRef, containerHeight } = useContainerHeight();
    console.log(data)
    return (
        <Box className="job-application-list" sx={{
            flex: 1, width: '100%',
            "& .MuiTableHead-root": {
                zIndex: 103
            },
            // second cell from row should have overflow visible to show the full name and the company name
            '& .MuiTableRow-root': {
                '& .MuiTableCell-root:nth-child(2)': {
                    zIndex: 102,
                    overflow: 'visible !important',
                    "& > .MuiBox-root": {
                        overflow: 'visible !important',
                    }
                },
            }
        }} ref={containerRef}>
            <DataTable
                rows={data.map(item => ({ ...item, id: String(item._id) }))}
                columns={columns}
                loading={loading}
                isLoadingMore={isLoadingMore}
                onScrollEnd={onScrollEnd}
                onSortModelChange={handleSortModelChange}
                checkboxSelection
                selectionModel={selectionModel}
                onSelectionModelChange={handleSelectionModelChange}
                pagination={{
                    currentPage: page,
                    pageSize,
                    totalRows: totalDocs,
                    onPageChange: handlePageChange,
                    onPageSizeChange: handlePageSizeChange,
                    pageSizeOptions: [10, 20, 50, 100],
                }}
                noDataComponent={emptyStateComponent}
                height={containerHeight}
                stickyHeader
                skeletonConfig={{
                    rowCount: 10,
                    animation: 'wave',
                }}
            />

            <FloatingSelectionBar
                selectedTalents={selectedTalentObjects}
                onClose={clearSelection}
                onSendMessage={handleSendMessage}
            />

            <TalentMediaDialog
                isOpen={mediaDialogState.isOpen}
                onClose={handleCloseMediaDialog}
                talentId={mediaDialogState.talentId}
                jobApplicationId={mediaDialogState.jobApplicationId}
                defaultTab={mediaDialogState.defaultTab}
                score={mediaDialogState.score}
                scoreExplainer={mediaDialogState.scoreExplainer}
                scoringStatus={mediaDialogState.scoringStatus}
                jobOfferId={mediaDialogState.jobOfferId}
                jobOfferTitle={mediaDialogState.jobOfferTitle}
                jobOfferNumber={mediaDialogState.jobOfferNumber}
            />

            <JobApplicationDetailDialog
                id={detailDialogId}
                onClose={() => setDetailDialogId(null)}
            />
        </Box>
    );
};

export default JobApplicationDataTable;
